/**
 * Draws the plate with the recorded mouths (docs/NEW-UI/09): the idle frame advances at 25 fps along the plate's
 * cycle, as the MuseTalk service does; while a clause plays, the mouth its audio clock asks for (mouth-timeline.ts)
 * is drawn over it, cross-faded with the one it leaves. No GPU, no network: JPEG bytes decoded as they are drawn.
 * `present`, when given, gets every frame drawn (a plate on a green screen: chroma-key.ts keys it onto the stage).
 */
import { mouthAt, type MouthState } from './mouth-timeline';
import type { MouthLibrary } from './mouth-library';
import type { PlateStore } from './plate';
import { FRAME_MS, type Mouth } from './visemes';

/** A clause being spoken: its mouths, and the audio clock in ms into it (null when it has not started). */
export interface Speaking {
  track: readonly Mouth[];
  clockMs: () => number | null;
}

const CACHE = 24; // decoded bitmaps kept: a few frames' worth of plate and mouths

export class MouthPlayer {
  private speaking: Speaking | null = null;
  private frame = 0;
  private started = performance.now();
  private raf = 0;
  private busy = false;
  private readonly bitmaps = new Map<string, ImageBitmap>();
  /** The last state drawn, for whoever shows it (the lab). */
  last: { idx: number; state: MouthState } | null = null;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly plate: PlateStore,
    private readonly mouths: MouthLibrary,
    private readonly present?: (frame: HTMLCanvasElement) => void,
  ) {
    canvas.width = plate.meta.size[0];
    canvas.height = plate.meta.size[1];
  }

  start(): void {
    const tick = () => {
      this.raf = requestAnimationFrame(tick);
      const frame = Math.floor((performance.now() - this.started) / FRAME_MS);
      if (frame !== this.frame && !this.busy) {
        this.frame = frame;
        void this.draw(frame % this.plate.meta.frames);
      }
    };
    this.raf = requestAnimationFrame(tick);
  }

  stop(): void {
    cancelAnimationFrame(this.raf);
    for (const b of this.bitmaps.values()) b.close();
    this.bitmaps.clear();
  }

  speak(speaking: Speaking | null): void {
    this.speaking = speaking;
  }

  private stateNow(): MouthState {
    const ms = this.speaking?.clockMs();
    if (!this.speaking || ms === null || ms === undefined) return { from: 'plate', to: 'plate', mix: 1 };
    const state = mouthAt(this.speaking.track, ms);
    // a mouth the library has not recorded (rest: the plate's own mouth) is the plate, faded like any other
    const own = (m: MouthState['from']) => (m !== 'plate' && !this.mouths.has(m) ? 'plate' : m);
    return { from: own(state.from), to: own(state.to), mix: state.mix };
  }

  private async draw(idx: number): Promise<void> {
    this.busy = true;
    try {
      const state = this.stateNow();
      const ctx = this.canvas.getContext('2d');
      if (!ctx) return;
      const base = await this.bitmap(`p${idx}`, () => this.plate.frameBytes(idx));
      const [x, y] = this.mouths.origin;
      ctx.globalAlpha = 1;
      ctx.drawImage(base, 0, 0);
      if (state.from !== 'plate' && this.mouths.has(state.from)) {
        ctx.drawImage(await this.mouthBitmap(state.from, idx), x, y);
      }
      if (state.to !== 'plate' && this.mouths.has(state.to)) {
        ctx.globalAlpha = state.mix;
        ctx.drawImage(await this.mouthBitmap(state.to, idx), x, y);
        ctx.globalAlpha = 1;
      } else if (state.to === 'plate' && state.from !== 'plate') {
        ctx.globalAlpha = state.mix; // fading back out: the plate's own mouth over the last one
        ctx.drawImage(base, x, y, this.mouths.meta.zona[2] - x, this.mouths.meta.zona[3] - y,
          x, y, this.mouths.meta.zona[2] - x, this.mouths.meta.zona[3] - y);
        ctx.globalAlpha = 1;
      }
      this.last = { idx, state };
      this.present?.(this.canvas);
    } finally {
      this.busy = false;
    }
  }

  private mouthBitmap(mouth: Mouth, idx: number): Promise<ImageBitmap> {
    return this.bitmap(`${mouth}${idx}`, () => this.mouths.crop(mouth, idx));
  }

  private async bitmap(key: string, bytes: () => ArrayBuffer): Promise<ImageBitmap> {
    const hit = this.bitmaps.get(key);
    if (hit) {
      // least recently used goes first: a bitmap still needed in this very draw (the plate, fading out) stays
      this.bitmaps.delete(key);
      this.bitmaps.set(key, hit);
      return hit;
    }
    const made = await createImageBitmap(new Blob([bytes()], { type: 'image/jpeg' }));
    this.bitmaps.set(key, made);
    if (this.bitmaps.size > CACHE) {
      const [oldest, b] = this.bitmaps.entries().next().value!;
      this.bitmaps.delete(oldest);
      b.close();
    }
    return made;
  }
}
